// Types and pure helpers for the Customer Support desk (spec 2026-10-03, release 1).
// Types mirror aamcirc/admin/routers/support.py; datetimes arrive as ISO strings.

export type DeskChannel = 'contact' | 'feedback' | 'email'
export type DeskState = 'to_review' | 'waiting' | 'snoozed' | 'done'
export type DeskTitle = 'flight_journal' | 'model_airplane_news' | 'rc_car_action' | 'diecast'
export type DeskBucket = DeskTitle | 'none'

export type DeskBucketSummary = {
  to_review: number; contact: number; feedback: number; email: number
  over_target: number; oldest_waiting_at: string | null
}

/** GET /api/support/summary */
export type DeskSummary = {
  total: number
  titles: Record<DeskBucket, DeskBucketSummary>
  drafts_ready: number
  cancellations_7d: number
}

/** One row of GET /api/support/conversations (and the PATCH response). */
export type DeskConversation = {
  channel: DeskChannel
  message_id: number
  publication: string | null
  name: string | null
  email: string | null
  snippet: string
  status: 'new' | 'read' | 'archived'
  state: DeskState
  received_at: string
  waiting_since: string
  topic: string | null
  draft_status: string | null
  assigned_to_user_id: number | null
  snoozed_until: string | null
  member: boolean
  assignee_email: string | null
  over_target: boolean
  target_hours: number
  waiting_hours: number
}

export type DeskConversationPage = { items: DeskConversation[]; total: number }

/** PATCH /api/support/conversations/{channel}/{id} body: send only the keys you change. */
export type DeskPatch = {
  assignee_user_id?: number | null
  snoozed_until?: string | null
  status?: 'read' | 'archived' | 'new'
  /** Email conversations only: the title it belongs to. */
  publication?: DeskTitle
}

export type DeskNote = { id: number; author_email: string | null; body: string; created_at: string }

export type StaffMember = { id: number; email: string }

export type SavedReply = {
  id: number
  name: string
  publication: string | null
  body_md: string
  created_by: string | null
  updated_at: string | null
  archived_at: string | null
}

export type DeskSetting = {
  publication: string
  first_review_hours: number
  default_owner_user_id: number | null
  wait_close_days: number
}

export type MemberMatch =
  | 'subscriber_id' | 'email' | 'account_number' | 'account_number_unconfirmed' | 'ambiguous' | 'none'

export type MemberContext = {
  person: { subscriber_id: number; name: string | null; email: string | null; account_number: string | null } | null
  match: MemberMatch | null
  memberships: {
    title: string
    status: string
    plan: string | null
    current_period_end: string | null
    auto_renew: boolean
    next_renewal_cents: number | null
    source: string // subscription_source: who bills it
    instrument: 'card' | 'bank'
    card: { brand: string; last4: string } | null
    member_since: string | null
  }[]
  payments: {
    at: string | null
    amount_cents: number | null
    title: string
    status: string | null
    kind: 'charge' | 'refund' | 'void'
  }[]
  earlier: { channel: DeskChannel; id: number; at: string; topic: string | null; status: string; publication: string | null }[]
  reading: {
    reading_days: number | null
    engaged_minutes: number | null
    score: number | null
    trend: { day: string; score: number | null }[]
  } | null
  writer_unverified: boolean
}

export const TITLE_ORDER: DeskTitle[] = ['flight_journal', 'model_airplane_news', 'rc_car_action', 'diecast']

export const TITLE_LABELS: Record<string, string> = {
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  rc_car_action: 'RC Car Action',
  diecast: 'Die Cast X',
  none: 'Needs a title',
}

/** How long a conversation has waited: "40m", "9h", "1d 9h", "2d", "12d". */
export function waitingLabel(hours: number): string {
  if (hours < 1) return `${Math.max(0, Math.floor(hours * 60))}m`
  if (hours < 24) return `${Math.floor(hours)}h`
  const days = Math.floor(hours / 24)
  if (days >= 7) return `${days}d`
  const h = Math.floor(hours - days * 24)
  return h === 0 ? `${days}d` : `${days}d ${h}h`
}

/** late = past the target; soon = past 75% of it. */
export function waitingTone(hours: number, targetHours: number): 'ok' | 'soon' | 'late' {
  if (hours > targetHours) return 'late'
  if (hours > 0.75 * targetHours) return 'soon'
  return 'ok'
}

const STATE_LABELS: Record<DeskState, string> = {
  to_review: 'To review', waiting: 'Waiting on customer', snoozed: 'Snoozed', done: 'Done',
}

export function stateLabel(state: DeskState): string {
  return STATE_LABELS[state] ?? state
}

export const TOPIC_LABELS: Record<string, string> = {
  billing: 'Billing', access: 'Access', viewer: 'Viewer', content: 'Content', account: 'Account',
  cancel: 'Cancellation', praise: 'Praise', other: 'Other', no_reply_needed: 'No reply needed',
}

/** The topic filter/count key for a conversation the drafter hasn't categorised yet (matches the API). */
export const NO_TOPIC = 'none'

/** Filter-chip order: what needs action first, then the rest, then "no reply needed" and uncategorised. */
export const TOPIC_ORDER = [
  'cancel', 'billing', 'access', 'account', 'viewer', 'content', 'praise', 'other', 'no_reply_needed', NO_TOPIC,
]

/** One colour per topic, the same on every desk page. Literal class strings so Tailwind keeps them. */
const TOPIC_TONES: Record<string, string> = {
  cancel: 'text-red-400 border-red-400/70 bg-red-400/10',
  billing: 'text-amber-300 border-amber-300/70 bg-amber-300/10',
  access: 'text-orange-400 border-orange-400/70 bg-orange-400/10',
  account: 'text-sky-400 border-sky-400/70 bg-sky-400/10',
  viewer: 'text-violet-400 border-violet-400/70 bg-violet-400/10',
  content: 'text-teal-400 border-teal-400/70 bg-teal-400/10',
  praise: 'text-green-400 border-green-400/70 bg-green-400/10',
  other: 'text-slate-300 border-slate-400/60 bg-slate-400/10',
  no_reply_needed: 'text-slate-500 border-slate-600 bg-transparent',
  [NO_TOPIC]: 'text-paper-dim border-dashed border-ink-500 bg-transparent',
}

/** Chip classes for a topic (null = not categorised yet); an unknown topic gets the "other" grey. */
export function topicTone(category: string | null): string {
  return TOPIC_TONES[category ?? NO_TOPIC] ?? TOPIC_TONES.other
}

/** A drafter category as staff read it; unknown categories are title-cased. */
export function topicLabel(category: string | null): string {
  if (!category || category === NO_TOPIC) return 'No topic yet'
  if (TOPIC_LABELS[category]) return TOPIC_LABELS[category]
  return category.split(/[_\s]+/).filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(' ')
}

/** Nav badge text: nothing for 0, "999+" above 999. */
export function badgeText(n: number): string {
  if (n <= 0) return ''
  return n > 999 ? '999+' : String(n)
}

export type SupportNavChild = { label: string; to: string; count?: number; badgeLabel?: string }

/** Children of the "Customer Support" menu item when the desk is on. `unthreaded` is the inbox's 30-day count of
 *  customer answers that failed to thread; the item appears only while it is above 0. */
export function supportNavChildren(summary: DeskSummary, { unthreaded = 0 }: { unthreaded?: number } = {}): SupportNavChild[] {
  const out: SupportNavChild[] = TITLE_ORDER.map(t => ({
    label: TITLE_LABELS[t], to: `/support/${t}`, count: summary.titles?.[t]?.to_review ?? 0,
  }))
  const none = summary.titles?.none?.to_review ?? 0
  if (none > 0) out.push({ label: TITLE_LABELS.none, to: '/support/none', count: none })
  if (unthreaded > 0) out.push({ label: 'Unthreaded', to: '/unthreaded', count: unthreaded, badgeLabel: 'unthreaded' })
  out.push(
    { label: 'Known issues', to: '/known-issues', count: 0 },
    { label: 'Saved replies', to: '/support/saved-replies', count: 0 },
    { label: 'Setup', to: '/support/setup', count: 0 },
    { label: 'Reply desk guide', to: '/help/reply-desk', count: 0 },
  )
  return out
}

/** A Setup address staff can forward customer messages to (publication null = every title). */
export type ForwardAddress = { id: number; label: string; email: string; publication: DeskTitle | null; internal: boolean }

/** Our own mail domains; anything else is "outside" and flagged before a forward goes. Matches the API. */
const INTERNAL_DOMAINS = ['airage.com', 'cirrata.com']

export function isInternalEmail(address: string): boolean {
  const domain = address.split('@').pop()?.trim().toLowerCase() ?? ''
  return INTERNAL_DOMAINS.some(d => domain === d || domain.endsWith('.' + d))
}

const EMAIL_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/

/** Split typed text ("a@x.com, b@y.com; c@z.com") into lower-cased valid addresses and the bits that aren't. */
export function parseAddresses(text: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = []
  const invalid: string[] = []
  for (const raw of text.split(/[\s,;]+/)) {
    const a = raw.trim().replace(/^<|>$/g, '')
    if (!a) continue
    if (EMAIL_RE.test(a)) { if (!valid.includes(a.toLowerCase())) valid.push(a.toLowerCase()) } else invalid.push(a)
  }
  return { valid, invalid }
}
